import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { fiscalNoteFormSchema, FiscalNoteFormData } from './schema'
import { BorrowerSelectionCard } from './borrower/borrower-selection-card'
import { useCompany } from '@/context/companies'
import { FiscalNoteData } from './fiscal-note-data'
import { Stack } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { forwardRef, useImperativeHandle } from 'react'
import { FiscalNote, TaxIdType } from '@/types'

type FiscalNoteFormProps = {
  onSubmit: (data: FiscalNoteFormData) => void
  loading: boolean
}

export type FiscalNoteFormRef = {
  reset: () => void
  setFiscalNote: (fiscalNote: FiscalNote) => void
}

export const FiscalNoteForm = forwardRef<
  FiscalNoteFormRef,
  FiscalNoteFormProps
>(({ onSubmit, loading }, ref) => {
  const { company } = useCompany()

  const form = useForm<FiscalNoteFormData>({
    resolver: zodResolver(fiscalNoteFormSchema),
    defaultValues: {
      borrower: {
        key: '',
        name: '',
        tax_id: '',
        tax_id_type: 'cpf',
      },
      value: '',
      service_code_id: '',
      cnae_id: '',
      service_tax_rate: '',
      description: '',
      iss_retention: false,
      state_uf: company.address?.state_code || null,
      municipality_ibge_code: company.address?.ibge_code || null,
    },
  })

  const { handleSubmit, setValue, reset } = form

  useImperativeHandle(ref, () => ({
    reset: () => reset(),
    setFiscalNote: (fiscalNote) => {
      setValue('borrower.key', fiscalNote.borrower.key)
      setValue('borrower.name', fiscalNote.borrower.name)
      setValue('borrower.tax_id', fiscalNote.borrower.tax_id)
      setValue(
        'borrower.tax_id_type',
        fiscalNote.borrower.tax_id_type as TaxIdType,
      )
      setValue('value', String(fiscalNote.value))
      setValue('service_code_id', String(fiscalNote.service_code.id))
      setValue('cnae_id', String(fiscalNote.cnae_id))
      setValue('description', fiscalNote.description)
      setValue('iss_retention', fiscalNote.iss_retention)
      setValue('state_uf', fiscalNote.municipality.state)
      setValue('municipality_ibge_code', fiscalNote.municipality.ibge_code)
    },
  }))

  return (
    <FormProvider {...form}>
      <Stack component="form" onSubmit={handleSubmit(onSubmit)} gap={2}>
        <BorrowerSelectionCard />

        <FiscalNoteData />

        <LoadingButton
          type="submit"
          size="large"
          fullWidth
          sx={{ mt: 2 }}
          loading={loading}
        >
          Emitir nota fiscal
        </LoadingButton>
      </Stack>
    </FormProvider>
  )
})
